import {
    CircleSponsorshipEligibilityResponse,
    CircleSponsorshipUpsertRequest,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import { queryKeys, useMutation, useQuery } from '@rhg/libs/react-query';
import { getRhgAppAppendHeader } from '@rhg/utils/browser';

export const partnerEligibilityQueryOptions = {
    queryKey: (
        partnerId?: string,
        request?: CircleSponsorshipUpsertRequest
    ) => {
        return [queryKeys.partnerEligibility, partnerId, request];
    },
    queryFn: async ({
        getAccessTokenSilently,
        partnerId,
        request,
        isSSO = false,
        isImplied = false,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        partnerId: string;
        request: CircleSponsorshipUpsertRequest;
        isSSO?: boolean;
        isImplied?: boolean;
    }): Promise<CircleSponsorshipEligibilityResponse | null> => {
        const circleApi = createCircleApi(getAccessTokenSilently);

        // Diagnostic funnel marker (EVE-12589): proves the validate call fired
        // and with which implied semantics.
        logger.info('partner.validate.request', {
            partnerId,
            implied: isImplied,
            isSSO,
        });

        const response = await circleApi
            .v1ValidatePartnerEligibility(
                {
                    partnerId,
                    circleSponsorshipEligibilityRequest: request,
                    implied: isImplied,
                },
                {
                    headers: getRhgAppAppendHeader({
                        registration_flow: isSSO ? 'sso' : 'direct',
                    }),
                }
            )
            .catch((e: CustomAxiosError) => {
                if (e?.response?.status === 404) {
                    // 404 is swallowed to null (treated as ineligible) — surface it.
                    logger.warn('partner.validate.404', {
                        partnerId,
                        implied: isImplied,
                    });
                    return { data: null };
                } else {
                    throw e;
                }
            });

        logger.info('partner.validate.result', {
            partnerId,
            hasEligibility: !!response?.data,
        });

        return response?.data;
    },
    queryOptions: (
        isAuthenticated: boolean,
        partnerId?: string,
        request?: CircleSponsorshipUpsertRequest
    ) => {
        return {
            enabled: !!(isAuthenticated && partnerId && request),
            retry: false,
            staleTime: 15 * MINUTE_MS,
        };
    },
};

export function usePartnerEligibility(
    partnerId?: string,
    request?: CircleSponsorshipUpsertRequest,
    isSSO = false,
    isImplied = false
) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    return usePartnerEligibilityBase({
        partnerId,
        request,
        getAccessTokenSilently,
        isAuthenticated,
        isSSO,
        isImplied,
    });
}

interface UsePartnerEligibilityBaseParams {
    partnerId?: string;
    request?: CircleSponsorshipUpsertRequest;
    getAccessTokenSilently: () => Promise<string>;
    isAuthenticated: boolean;
    isSSO?: boolean;
    isImplied?: boolean;
}
export function usePartnerEligibilityBase({
    partnerId,
    request,
    getAccessTokenSilently,
    isAuthenticated,
    isSSO = false,
    isImplied = false,
}: UsePartnerEligibilityBaseParams) {
    const { queryKey, queryFn, queryOptions } = partnerEligibilityQueryOptions;

    const options = queryOptions(isAuthenticated, partnerId, request);

    const { data, ...otherProps } = useQuery<
        CircleSponsorshipEligibilityResponse | null | undefined
    >({
        queryKey: queryKey(partnerId, request),
        queryFn: async () => {
            if (!request) return;
            return queryFn({
                getAccessTokenSilently,
                partnerId: partnerId!,
                request,
                isSSO,
                isImplied,
            });
        },
        ...options,
        // blockRefetches
        refetchInterval: false,
        refetchIntervalInBackground: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    return {
        eligibility: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useGetPartnerEligibility() {
    const { getAccessTokenSilently } = useAuth();
    const { queryFn } = partnerEligibilityQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            partnerId,
            request,
            isSSO,
            isImplied,
        }: {
            partnerId: string;
            request: CircleSponsorshipUpsertRequest;
            isSSO?: boolean;
            isImplied?: boolean;
        }) => {
            return queryFn({
                getAccessTokenSilently,
                partnerId,
                request,
                isSSO,
                isImplied,
            });
        },
    });

    return {
        getPartnerEligibility: mutate,
        getPartnerEligibilityAsync: mutateAsync,
        ...otherProps,
    };
}
